// Phase 86AZ.60 — Ultra Admin: Müteahhit Önericileri (Contractor Recommenders)
// ===========================================================================
// /ultra-admin/muteahhit/onericiler — manage who has the right to recommend
// contractor candidates. Just name + email. Each entry receives an
// invitation email with their private /muteahhit-oner/:token link (1-year).
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { apiClient } from '../lib/apiClient';
import {
  Users, Plus, Search, RefreshCw, Trash2, Send, Mail, User,
  Calendar, Hash, X, AlertCircle, CheckCircle2, Bell,
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import AdminSidebar from '../components/AdminSidebar';

export default function UltraAdminContractorRecommenders() {
  const { language } = useLanguage();
  const tr = language === 'tr';

  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [modal,   setModal]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/ultra-admin/contractor-recommenders');
      setRows(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yüklenemedi.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openModal = () => setModal({ full_name: '', email: '', loading: false, error: '', fieldErrors: {} });

  const handleCreate = async () => {
    const fe = {};
    if (!modal.full_name.trim())   fe.full_name = tr ? 'Ad-soyad zorunludur.' : 'Full name required.';
    if (!modal.email.trim())       fe.email     = tr ? 'E-posta zorunludur.'  : 'Email required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(modal.email)) fe.email = tr ? 'Geçerli e-posta giriniz.' : 'Invalid email.';
    if (Object.keys(fe).length) { setModal(m => ({ ...m, fieldErrors: fe })); return; }
    setModal(m => ({ ...m, loading: true, error: '', fieldErrors: {} }));
    try {
      const r = await apiClient.post('/ultra-admin/contractor-recommenders', {
        full_name: modal.full_name.trim(),
        email:     modal.email.trim().toLowerCase(),
      });
      setRows(prev => [r.data, ...prev]);
      setModal(null);
      toast.success(r.data?.email_sent
        ? (tr ? 'Önerici oluşturuldu. Davet e-postası gönderildi.' : 'Recommender created. Invite emailed.')
        : (tr ? 'Önerici oluşturuldu fakat e-posta gönderilemedi.' : 'Recommender created but email failed.'));
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setModal(m => ({ ...m, loading: false, error: typeof msg === 'string' ? msg : 'Hata' }));
    }
  };

  const handleResend = async (rec) => {
    try {
      await apiClient.post(`/ultra-admin/contractor-recommenders/${rec.id}/resend-invite`);
      toast.success(tr ? `Davet yeniden gönderildi: ${rec.email}` : `Invite re-sent: ${rec.email}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gönderilemedi.');
    }
  };

  const handleDelete = async (rec) => {
    if (!window.confirm(tr ? `"${rec.full_name}" önericisini silmek istediğinize emin misiniz? Önceki önerileri korunacak.` : `Delete recommender "${rec.full_name}"? Their past recommendations stay.`)) return;
    try {
      await apiClient.delete(`/ultra-admin/contractor-recommenders/${rec.id}`);
      setRows(prev => prev.filter(r => r.id !== rec.id));
      toast.success(tr ? 'Önerici silindi.' : 'Recommender deleted.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi.');
    }
  };

  const filtered = search
    ? rows.filter(r => {
        const q = search.toLowerCase();
        return (r.full_name || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q);
      })
    : rows;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <div className="flex-1 overflow-x-hidden">
        <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="muteahhit-onericiler-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-2xl bg-red-100 flex items-center justify-center flex-shrink-0">
            <Users className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h1 className="font-playfair text-2xl text-[#0A3A5A] font-medium">{tr ? 'Tedarikçi Önericileri' : 'Supplier Recommenders'}</h1>
            <p className="text-sm text-gray-500 mt-0.5 max-w-xl">{tr ? 'Bu kişiler size tedarikçi firma önerisinde bulunma yetkisine sahiptir. Eklediğinizde otomatik davet e-postası ile özel öneri linki gönderilir (1 yıl geçerli).' : 'These people are authorized to recommend supplier firms to you. They receive an invite email with their personal 1-year submission link.'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" className="rounded-full" data-testid="onerici-refresh">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {tr ? 'Yenile' : 'Refresh'}
          </Button>
          <Button onClick={openModal} className="bg-red-600 hover:bg-red-700 text-white rounded-full" data-testid="onerici-new-btn">
            <Plus className="h-4 w-4 mr-2" />
            {tr ? 'Yeni Önerici' : 'New Recommender'}
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-2xl mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input data-testid="onerici-search" placeholder={tr ? 'Ad veya e-posta ara...' : 'Search by name or email...'} value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-xl text-sm" />
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-[rgba(10,58,90,0.08)] rounded-2xl p-4">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">{tr ? 'Toplam Önerici' : 'Total Recommenders'}</p>
          <p className="font-playfair text-2xl text-[#0A3A5A] mt-1">{rows.length}</p>
        </div>
        <div className="bg-white border border-[rgba(10,58,90,0.08)] rounded-2xl p-4">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">{tr ? 'Toplam Öneri' : 'Total Recommendations'}</p>
          <p className="font-playfair text-2xl text-red-600 mt-1">{rows.reduce((s, r) => s + (r.submissions_count || 0), 0)}</p>
        </div>
        <div className="bg-white border border-[rgba(10,58,90,0.08)] rounded-2xl p-4">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">{tr ? 'En Aktif' : 'Most Active'}</p>
          <p className="font-playfair text-lg text-emerald-700 mt-1 truncate">
            {rows.length === 0 ? '—' : [...rows].sort((a, b) => (b.submissions_count || 0) - (a.submissions_count || 0))[0].full_name}
          </p>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-2xl overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">{tr ? 'Yükleniyor...' : 'Loading...'}</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 mb-4">{search ? (tr ? 'Aramayla eşleşen kayıt yok.' : 'No matching records.') : (tr ? 'Henüz önerici eklenmemiş. İlk önericiyi oluşturarak başlayın.' : 'No recommenders yet. Add your first one to get started.')}</p>
            {!search && (
              <Button onClick={openModal} className="bg-red-600 hover:bg-red-700 text-white rounded-full">
                <Plus className="h-4 w-4 mr-2" />{tr ? 'İlk Önericiyi Ekle' : 'Add First Recommender'}
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3 font-semibold">{tr ? 'Ad Soyad' : 'Full Name'}</th>
                  <th className="px-4 py-3 font-semibold">{tr ? 'E-posta' : 'Email'}</th>
                  <th className="px-4 py-3 font-semibold">{tr ? 'Toplam Öneri' : 'Recommendations'}</th>
                  <th className="px-4 py-3 font-semibold">{tr ? 'Eklendi' : 'Added'}</th>
                  <th className="px-4 py-3 font-semibold">{tr ? 'Son Davet' : 'Last Invite'}</th>
                  <th className="px-4 py-3 font-semibold text-right"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} className="border-b border-gray-100 last:border-0 hover:bg-red-50/30 transition-colors" data-testid={`onerici-row-${r.id}`}>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                          {(r.full_name || '?').charAt(0).toUpperCase()}
                        </div>
                        <span className="font-semibold text-gray-800">{r.full_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-gray-700 text-xs">{r.email}</span>
                    </td>
                    <td className="px-4 py-4">
                      {(r.submissions_count || 0) > 0
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-700">
                            <Hash className="h-3 w-3" /> {r.submissions_count}
                          </span>
                        : <span className="text-xs text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-4 text-xs text-gray-500"><span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{(r.created_at || '').slice(0, 10)}</span></td>
                    <td className="px-4 py-4 text-xs text-gray-500">{r.last_invited_at ? (r.last_invited_at || '').slice(0, 10) : <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleResend(r)} className="text-red-700 hover:bg-red-50 rounded-full h-7 w-7 p-0" title={tr ? 'Daveti Yeniden Gönder' : 'Resend Invite'} data-testid={`onerici-resend-${r.id}`}>
                          {r.last_invited_at ? <Bell className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(r)} className="text-red-600 hover:bg-red-50 rounded-full h-7 w-7 p-0" title={tr ? 'Sil' : 'Delete'} data-testid={`onerici-delete-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create modal */}
      {modal && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/55 p-4" data-testid="onerici-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center"><Plus className="w-6 h-6 text-red-700" /></div>
                <div>
                  <h3 className="font-bold text-gray-800 text-lg">{tr ? 'Yeni Önerici' : 'New Recommender'}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{tr ? 'Ad-soyad + e-posta. Davet e-postası otomatik gider.' : 'Name + email. Invite email is sent automatically.'}</p>
                </div>
              </div>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold text-gray-600">{tr ? 'Ad Soyad' : 'Full Name'} *</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input data-testid="onerici-modal-fullname" className={`pl-9 ${modal.fieldErrors?.full_name ? 'border-red-400' : ''}`} value={modal.full_name} onChange={e => setModal(m => ({ ...m, full_name: e.target.value, fieldErrors: { ...m.fieldErrors, full_name: undefined } }))} />
                </div>
                {modal.fieldErrors?.full_name && <p className="text-[11px] text-rose-600 mt-1">{modal.fieldErrors.full_name}</p>}
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">{tr ? 'E-posta' : 'Email'} *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input data-testid="onerici-modal-email" type="email" className={`pl-9 ${modal.fieldErrors?.email ? 'border-red-400' : ''}`} value={modal.email} onChange={e => setModal(m => ({ ...m, email: e.target.value, fieldErrors: { ...m.fieldErrors, email: undefined } }))} />
                </div>
                {modal.fieldErrors?.email && <p className="text-[11px] text-rose-600 mt-1">{modal.fieldErrors.email}</p>}
              </div>
            </div>

            {modal.error && <p className="text-xs text-rose-600 mt-3" data-testid="onerici-modal-error">{modal.error}</p>}

            <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800 leading-relaxed">
                {tr ? 'Önericiye e-posta ile 1 yıl geçerli kişisel öneri sayfası bağlantısı gönderilecektir. Önerici, /tedarikci/aday-oner sayfasında tedarikçi firma önerileri gönderebilir.' : 'The recommender will receive an email with their personal 1-year submission link to /tedarikci/aday-oner.'}
              </p>
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={() => setModal(null)} disabled={modal.loading} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50" data-testid="onerici-modal-cancel">
                {tr ? 'Vazgeç' : 'Cancel'}
              </button>
              <button onClick={handleCreate} disabled={modal.loading} className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50" data-testid="onerici-modal-confirm">
                {modal.loading ? (tr ? 'Oluşturuluyor…' : 'Creating…') : (tr ? 'Oluştur & Davet Gönder' : 'Create & Send Invite')}
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}
